import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import dayjs from 'dayjs';

import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import ApprovalStatusChip from 'components/table/ApprovalStatusChip';
import CurrencyAmount from 'components/currency/CurrencyAmount';
import TableActionButton from 'components/table/TableActionButton';
import TableActions from 'components/table/TableActions';
import TableActionCell from 'components/table/TableActionCell';
import ClickableTableRow, { stopRowClickPropagation } from 'components/table/ClickableTableRow';
import { orderStatusChipSx, paymentStatusChipSx } from 'components/table/tableChipStyles';
import { useToast } from 'contexts/ToastContext';
import { listRefundRequests } from 'services/payment.service';
import { brand } from 'utils/designTokens';

import RefundRequestDetailsDialog from './RefundRequestDetailsDialog';

const STATUS_FILTERS = [
  { value: 'all', label: 'All Requests' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' }
];

export default function RefundRequestList() {
  const { showApiError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Dialog state
  const [detailsDialog, setDetailsDialog] = useState({ open: false, id: null });

  // Deep-link integration: when opened from FCM push notification (e.g. ?requestId=... or ?id=...)
  useEffect(() => {
    const targetRequestId = searchParams.get('requestId') || searchParams.get('id');
    if (targetRequestId) {
      setDetailsDialog({ open: true, id: targetRequestId });
    }
  }, [searchParams]);

  const handleCloseDetailsDialog = () => {
    setDetailsDialog({ open: false, id: null });
    if (searchParams.get('requestId') || searchParams.get('id')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('requestId');
      nextParams.delete('id');
      setSearchParams(nextParams, { replace: true });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(debouncedSearch);
      if (debouncedSearch && isUuid) {
        params.order_id = debouncedSearch;
      }

      const res = await listRefundRequests(params);
      const data = res?.data;
      const list = Array.isArray(data?.requests) ? data.requests : [];

      // If client search text matches customer name/mobile/order_no, filter in memory
      let filtered = list;
      if (debouncedSearch && !isUuid) {
        const q = debouncedSearch.toLowerCase();
        filtered = list.filter(
          (r) =>
            String(r.order_no || r.order_id).toLowerCase().includes(q) ||
            String(r.customer?.name || '').toLowerCase().includes(q) ||
            String(r.customer?.mobile_number || '').includes(q) ||
            String(r.reason || '').toLowerCase().includes(q)
        );
      }

      setRequests(filtered);
      setTotal(debouncedSearch && !isUuid ? filtered.length : (Number(data?.total) || filtered.length));
    } catch (err) {
      showApiError(err, 'Failed to load refund requests.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, debouncedSearch, showApiError]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (val) => {
    setStatusFilter(val);
    setPage(0);
  };

  const openDetails = (id) => {
    setDetailsDialog({ open: true, id });
  };

  return (
    <Box>
      <PageHeader
        title="Refund Requests"
        subtitle="Monitor customer refund requests and reconciliation history."
        count={total}
        actions={
          <Tooltip title="Refresh requests">
            <span>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={loadRequests}
                disabled={loading}
                startIcon={<ReloadOutlined spin={loading} />}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>
        }
      />

      <MainCard content={false}>
        {/* Filter Controls Bar */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${brand.border}` }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {STATUS_FILTERS.map((f) => (
                <Chip
                  key={f.value}
                  label={f.label}
                  clickable
                  onClick={() => handleStatusFilterChange(f.value)}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderRadius: 1.5,
                    bgcolor: statusFilter === f.value ? brand.primary : 'rgba(31, 91, 64, 0.06)',
                    color: statusFilter === f.value ? '#ffffff' : 'text.primary',
                    border: `1px solid ${
                      statusFilter === f.value ? brand.primary : 'rgba(31, 91, 64, 0.14)'
                    }`,
                    '&:hover': {
                      bgcolor: statusFilter === f.value ? brand.primaryDark : 'rgba(31, 91, 64, 0.12)'
                    }
                  }}
                />
              ))}
            </Stack>

            <TextField
              size="small"
              placeholder="Search by order #, customer, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 300 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined style={{ color: '#8c8c8c' }} />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Stack>
        </Box>

        {/* Requests Table */}
        <TableContainer sx={{ minHeight: 380, maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: brand.canvas, fontWeight: 700 } }}>
                <TableCell width={50}>#</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Paid Amount</TableCell>
                <TableCell align="right">Requested Refund</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Decision</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center" width={80}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Loading refund requests...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                      No refund requests found.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {statusFilter !== 'all'
                        ? `No requests matching status '${statusFilter}'.`
                        : 'Customer refund requests will appear here when submitted.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req, idx) => {
                  return (
                    <ClickableTableRow
                      key={req.id || idx}
                      onClick={() => openDetails(req.id)}
                    >
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {page * rowsPerPage + idx + 1}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {req.order_no || `#${req.order_id}`}
                        </Typography>
                        {req.payment_status && (
                          <Chip
                            label={req.payment_status}
                            size="small"
                            sx={{ mt: 0.5, ...paymentStatusChipSx(req.payment_status) }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {req.customer?.name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {req.customer?.mobile_number || '—'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {req.order_status ? (
                          <Chip
                            label={req.order_status}
                            size="small"
                            sx={orderStatusChipSx(req.order_status)}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <CurrencyAmount value={req.paid_amount} fontWeight={600} />
                      </TableCell>

                      <TableCell align="right">
                        <CurrencyAmount
                          value={req.requested_amount}
                          fontWeight={700}
                          color="primary.main"
                        />
                      </TableCell>

                      <TableCell sx={{ maxWidth: 180 }}>
                        <Typography
                          variant="caption"
                          color="text.primary"
                          noWrap
                          display="block"
                          title={req.reason}
                        >
                          {req.reason || '—'}
                        </Typography>
                        {req.admin_note && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                            title={`Admin: ${req.admin_note}`}
                          >
                            Admin: {req.admin_note}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <ApprovalStatusChip status={req.status} />
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">
                          {req.createdAt ? dayjs(req.createdAt).format('YYYY-MM-DD') : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {req.createdAt ? dayjs(req.createdAt).format('HH:mm') : ''}
                        </Typography>
                      </TableCell>

                      <TableActionCell>
                        <TableActions>
                          <TableActionButton
                            action="view"
                            title="View Details"
                            onClick={stopRowClickPropagation(() => openDetails(req.id))}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </TableActionButton>
                        </TableActions>
                      </TableActionCell>
                    </ClickableTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ borderTop: `1px solid ${brand.border}` }}
        />
      </MainCard>

      {/* Dialogs */}
      <RefundRequestDetailsDialog
        open={detailsDialog.open}
        requestId={detailsDialog.id}
        onClose={handleCloseDetailsDialog}
      />
    </Box>
  );
}
